import { useEffect, useRef, useState } from 'preact/hooks';
import type { DropClient, Peer, ShareLink, TextMessage, Transfer } from '../core/contract';
import { Avatar, Icon } from './icons';
import { Sheet } from './Sheet';
import {
  Completed,
  CreateShareLinkPanel,
  DropRecipientPanel,
  HistoryPanel,
  Incoming,
  PairPanel,
  QR,
  RoomPanel,
  SendLaterPanel,
  SettingsPanel,
  ShareLinkPanel,
  ShareRecipientPanel,
  TextPanel,
} from './panels';
import { bytes, chime, eta, friendlyPlatform, haptic, unlockSound } from './utils';
type Panel = {
  type:
    | 'send-options'
    | 'pair'
    | 'room'
    | 'settings'
    | 'history'
    | 'incoming'
    | 'completed'
    | 'text'
    | 'message'
    | 'share-create'
    | 'share'
    | 'share-recipient'
    | 'send-later'
    | 'drop-recipient';
  id?: string;
  ids?: string[];
  code?: string;
  files?: File[];
  shareLink?: ShareLink;
};
const activeStates = ['waiting', 'transferring', 'paused'];
function PeerButton({
  peer,
  index,
  count = 4,
  transfer,
  selecting,
  selected,
  leaving = false,
  onClick,
  onText,
  onDrop
}: {
  peer: Peer;
  index: number;
  count?: number;
  transfer?: Transfer;
  selecting: boolean;
  selected: boolean;
  leaving?: boolean;
  onClick: () => void;
  onText: () => void;
  onDrop: (files: File[]) => void;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined),
    held = useRef(false);
  const [drag, setDrag] = useState(false);
  const cancelHold = () => clearTimeout(timer.current);
  useEffect(() => () => cancelHold(), []);
  const progress = transfer ? Math.min(1, transfer.bytesDone / (transfer.totalBytes || 1)) : 0;
  return <button data-peer-id={peer.id} class={`peer peer-${index % 8} ${!peer.online ? 'offline' : ''} ${leaving ? 'leaving' : ''} ${selected ? 'selected' : ''} ${drag ? 'drag-hover' : ''}`} style={{
    '--peer-index': index,
    ...(count > 4 ? {
      '--x': `${index % 2 ? 79 : 21}%`,
      '--y': `${(count > 8 ? [8, 24, 40, 60, 76, 92] : count > 6 ? [12, 36, 64, 88] : [17, 50, 83])[Math.floor(index / 2)]}%`
    } : {})
  }} aria-label={`${peer.name}, ${friendlyPlatform(peer.platform)}${peer.online ? selecting ? ', select device' : ', send files' : peer.sources.includes('paired') ? ', offline' : ', reconnecting'}`} aria-pressed={selecting ? selected : undefined} aria-disabled={!peer.online} aria-hidden={leaving || undefined} tabIndex={leaving ? -1 : 0} onClick={() => {
    if (!held.current) onClick();
  }} onContextMenu={e => {
    e.preventDefault();
    if (peer.online) onText();
  }} onPointerDown={e => {
    held.current = false;
    if (e.button !== 0 || !peer.online) return;
    timer.current = setTimeout(() => {
      held.current = true;
      haptic();
      onText();
    }, 550);
  }} onPointerUp={cancelHold} onPointerCancel={cancelHold} onPointerLeave={cancelHold} onPointerMove={e => {
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 8) cancelHold();
  }} onDragOver={e => {
    if (e.dataTransfer?.types.includes('Files') && peer.online) {
      e.preventDefault();
      e.stopPropagation();
      setDrag(true);
    }
  }} onDragLeave={() => setDrag(false)} onDrop={e => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
    if (peer.online && e.dataTransfer) onDrop([...e.dataTransfer.files]);
  }}>
    <span class="peer-face"><Avatar animal={peer.avatar} name={peer.name} color={peer.color} size={76} />{transfer && <svg class="progress-ring" viewBox="0 0 80 80" aria-hidden="true"><circle class="progress-track" cx="40" cy="40" r="37" /><circle cx="40" cy="40" r="37" pathLength="100" stroke-dasharray="100" stroke-dashoffset={100 - progress * 100} /></svg>}{selected ? <span class="peer-indicator selected-indicator"><Icon name="check" size={12} /></span> : peer.sources.includes('paired') && peer.online ? <span class="peer-indicator"><Icon name="link" size={11} /></span> : peer.sources.includes('room') ? <span class="peer-indicator"><Icon name="plus" size={11} /></span> : null}</span><strong>{peer.name}</strong><span class="peer-platform">{peer.online ? friendlyPlatform(peer.platform) : peer.sources.includes('paired') ? 'Paired · Offline' : 'Reconnecting…'}</span>{transfer && <span class="peer-progress">{transfer.state === 'waiting' ? 'Waiting…' : transfer.state === 'paused' ? 'Reconnecting…' : `${Math.round(progress * 100)}%`}</span>}
  </button>;
}
export function App({
  client,
  sharedFiles = [],
  initialHash = ''
}: {
  client: DropClient;
  sharedFiles?: File[];
  initialHash?: string;
}) {
  const [state, setState] = useState(client.getState()),
    [panel, setPanel] = useState<Panel | null>(null),
    [queue, setQueue] = useState<Panel[]>([]),
    [selection, setSelection] = useState<string[]>([]),
    [selecting, setSelecting] = useState(false),
    [pending, setPending] = useState<File[]>(sharedFiles),
    [pastedText, setPastedText] = useState(''),
    [toast, setToast] = useState(''),
    [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null),
    folderInput = useRef<HTMLInputElement>(null),
    targetIds = useRef<string[]>([]),
    sentFiles = useRef(new Map<string, File[]>()),
    toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined),
    dragDepth = useRef(0),
    pendingAction = useRef<'send' | 'share' | 'drop'>('send');
  const chooseFilesForShare = () => setPanel({ type: 'share-create', files: pending });
  const chooseFilesForDrop = () => setPanel({ type: 'send-later', files: pending });
  const pickFor = (action: 'send' | 'share' | 'drop') => {
    pendingAction.current = action;
    targetIds.current = [];
    fileInput.current?.click();
  };
  const seen = useRef(
    new Set([
      ...state.transfers.filter((t) => t.state !== 'incoming').map((t) => `${t.id}:${t.state}`),
      ...state.messages.map((m) => m.id),
    ]),
  );
  const notice = (message: string) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  };
  const [radarPeers, setRadarPeers] = useState(() => state.peers.map((peer, slot) => ({
    peer,
    slot,
    leaving: false
  })));
  useEffect(() => {
    const unsubscribe = client.subscribe(setState);
    setState(client.getState());
    return unsubscribe;
  }, [client]);
  useEffect(() => {
    setRadarPeers(previous => {
      const next = previous.map(entry => {
        const peer = state.peers.find(p => p.id === entry.peer.id);
        return peer ? {
          ...entry,
          peer,
          leaving: false
        } : {
          ...entry,
          leaving: true
        };
      });
      for (const peer of state.peers) if (!next.some(entry => entry.peer.id === peer.id)) {
        let slot = 0;
        while (next.some(entry => entry.slot === slot)) slot++;
        next.push({
          peer,
          slot,
          leaving: false
        });
      }
      return next.sort((a, b) => a.slot - b.slot);
    });
    const timer = setTimeout(() => setRadarPeers(entries => entries.filter(entry => !entry.leaving)), 220);
    return () => clearTimeout(timer);
  }, [state.peers]);
  useEffect(() => {
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = state.settings.theme === 'dark' || state.settings.theme === 'system' && mq.matches;
      document.documentElement.dataset.theme = dark ? 'dark' : 'light';
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#14171d' : '#fafbf9');
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [state.settings.theme]);
  useEffect(() => {
    const consumeHash = (value: string) => {
      const hash = new URLSearchParams(value.slice(1));
      if (!value && !/^\/(s|d)\//.test(location.pathname)) setPanel(null);
      if (hash.has('pair') || hash.has('room')) {
        history.replaceState(null, '', `${location.pathname}${location.search}`);
      }
      if (hash.has('pair')) {
        setPanel({
          type: 'pair',
          code: hash.get('pair') || '',
        });
      }
      if (hash.has('room')) {
        void client
          .joinRoom(hash.get('room') || '')
          .then(() => {
            notice('You joined the room');
            setPanel({ type: 'room' });
          })
          .catch((e) => {
            notice(e.message);
            setPanel({ type: 'room' });
          });
      }
      if (hash.has('s')) {
        setPanel({
          type: 'share-recipient',
          id: hash.get('s') || '',
        });
      }
      if (hash.has('drop')) {
        setPanel({
          type: 'drop-recipient',
          id: hash.get('drop') || '',
          code: hash.get('k') || '',
        });
      }
      const sharePath = location.pathname.match(/^\/s\/([a-zA-Z0-9_-]+)/);
      if (sharePath) {
        setPanel({
          type: 'share-recipient',
          id: sharePath[1],
        });
      }
      const dropPath = location.pathname.match(/^\/d\/([a-zA-Z0-9_-]+)/);
      if (dropPath) {
        setPanel({
          type: 'drop-recipient',
          id: dropPath[1],
          code: location.hash.replace(/^#/, ''),
        });
      }
    };
    consumeHash(initialHash);
    const hashChange = () => consumeHash(location.hash);
    window.addEventListener('hashchange', hashChange);
    const wakeSound = () => {
      if (client.getState().settings.sounds) unlockSound();
    };
    document.addEventListener('pointerdown', wakeSound);
    document.addEventListener('keydown', wakeSound);
    return () => {
      window.removeEventListener('hashchange', hashChange);
      document.removeEventListener('pointerdown', wakeSound);
      document.removeEventListener('keydown', wakeSound);
    };
  }, []);
  useEffect(() => {
    const additions: Panel[] = [];
    for (const t of state.transfers) {
      const key = `${t.id}:${t.state}`;
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      const shownInShare = panel?.type === 'share-recipient' && state.peers.some(p => p.id === t.peerId && p.sources.includes('link'));
      if (t.state === 'incoming' && !shownInShare) {
        chime(state.settings.sounds);
        additions.push({
          type: 'incoming',
          id: t.id
        });
      }
      if (t.state === 'done') {
        chime(state.settings.sounds, true);
        if (t.direction === 'receive' && !shownInShare) additions.push({
          type: 'completed',
          id: t.id
        });else if (t.direction === 'send') notice(`Delivered to ${t.peerName}`);
      }
      if (t.state === 'failed') notice(t.error || `Couldn’t finish sending to ${t.peerName}`);
    }
    for (const m of state.messages) if (!seen.current.has(m.id)) {
      seen.current.add(m.id);
      if (m.direction === 'receive') {
        chime(state.settings.sounds);
        additions.push({
          type: 'message',
          id: m.id
        });
      }
    }
    if (additions.length) setQueue(q => [...q, ...additions]);
  }, [state.transfers, state.messages]);
  useEffect(() => {
    if (!panel && queue.length) {
      const next = queue[0];
      setQueue(queue.slice(1));
      if (next.type !== 'incoming' || state.transfers.some(t => t.id === next.id && t.state === 'incoming')) setPanel(next);
    }
  }, [panel, queue]);
  useEffect(() => {
    if (panel?.type === 'incoming' && !state.transfers.some(t => t.id === panel.id && t.state === 'incoming')) setPanel(null);
  }, [state.transfers]);
  const send = async (ids: string[], files: File[]) => {
    if (!files.length || !ids.length) return;
    try {
      const previousIds = new Set(client.getState().transfers.map(t => t.id));
      await client.sendFiles(ids, files);
      for (const transfer of client.getState().transfers) if (!previousIds.has(transfer.id) && transfer.direction === 'send' && ids.includes(transfer.peerId)) sentFiles.current.set(transfer.id, files);
      setPending([]);
      setSelection([]);
      setSelecting(false);
      haptic();
      notice(`Ready to send to ${ids.length === 1 ? state.peers.find(p => p.id === ids[0])?.name || 'your device' : `${ids.length} devices`}`);
    } catch (e) {
      notice(e instanceof Error ? e.message : 'Could not send these files.');
    }
  };
  const sendText = async (ids: string[], text: string) => {
    try {
      await client.sendText(ids, text);
      setPastedText('');
      setSelection([]);
      setSelecting(false);
      notice('Note sent');
    } catch (e) {
      notice(e instanceof Error ? e.message : 'Could not send your note.');
    }
  };
  const chooseFiles = (ids: string[], folder = false) => {
    pendingAction.current = 'send';
    targetIds.current = ids;
    (folder ? folderInput : fileInput).current?.click();
  };
  const handlePeer = (peer: Peer) => {
    if (!peer.online) {
      notice(peer.sources.includes('paired') ? `${peer.name} is offline. Open Drop on that device to connect.` : `${peer.name} is reconnecting. Give it a moment.`);
      return;
    }
    if (selecting) {
      setSelection(ids => ids.includes(peer.id) ? ids.filter(id => id !== peer.id) : [...ids, peer.id]);
      return;
    }
    if (pending.length) void send([peer.id], pending);else if (pastedText) void sendText([peer.id], pastedText);else chooseFiles([peer.id]);
  };
  useEffect(() => {
    const paste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement;
      if (target instanceof HTMLElement && target.closest('input,textarea,[contenteditable="true"]') || document.querySelector('[role="dialog"]')) return;
      const files = [...(event.clipboardData?.files || [])];
      const text = event.clipboardData?.getData('text/plain');
      if (files.length) {
        event.preventDefault();
        if (selection.length) void send(selection, files);else {
          setPending(files);
          setPastedText('');
          notice('Choose a device for your pasted files');
        }
      } else if (text) {
        event.preventDefault();
        if (selection.length) void sendText(selection, text);else {
          setPastedText(text);
          setPending([]);
          notice('Choose a device for your pasted text');
        }
      }
    };
    document.addEventListener('paste', paste);
    return () => document.removeEventListener('paste', paste);
  }, [selection, state.peers, panel]);
  useEffect(() => {
    if (new URLSearchParams(location.search).has('share-error')) notice('These files could not be opened. Try sharing a smaller selection.');
    if (sharedFiles.length) notice(`${sharedFiles.length} ${sharedFiles.length === 1 ? 'file' : 'files'} ready. Choose a device.`);
  }, []);
  const active = state.transfers.filter(t => activeStates.includes(t.state)),
    incoming = state.transfers.filter(t => t.state === 'incoming');
  const ready = state.peers.filter(p => p.online).length;
  const radarCount = Math.min(12, Math.max(4, ...radarPeers.map(entry => entry.slot + 1)));
  const invite = () => {
    if (state.room) setPanel({
      type: 'room'
    });else void client.createRoom().then(() => setPanel({
      type: 'room'
    })).catch(e => notice(e.message));
  };
  const transfer = panel?.id ? state.transfers.find(t => t.id === panel.id) : undefined,
    message = panel?.id ? state.messages.find(m => m.id === panel.id) : undefined;
  const close = () => setPanel(null);
  const resend = (t: Transfer) => {
    close();
    const peer = state.peers.find(p => p.id === t.peerId);
    if (!peer?.online) {
      notice('Open Drop on the other device to send again.');
      return;
    }
    const files = sentFiles.current.get(t.id);
    if (files) void send([t.peerId], files);else {
      notice('Choose the original files to send again');
      chooseFiles([t.peerId]);
    }
  };
  const titles: Record<Panel['type'], string> = {
    'send-options': 'Pass something along.',
    pair: 'A lasting connection.',
    room: state.room ? 'Make yourself at home.' : 'Meet in a room.',
    settings: 'Your little corner.',
    history: 'Recently shared.',
    incoming: 'Something for you.',
    completed: 'Made it, just like that.',
    text: 'Send a little note.',
    message: 'A note for you.',
    'share-create': 'Create a share link.',
    share: 'Direct share link.',
    'share-recipient': 'Shared files.',
    'send-later': 'Send now. Open later.',
    'drop-recipient': 'Encrypted drop.',
  };
  return <div class="app" onDragEnter={e => {
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault();
      dragDepth.current++;
      setDragging(true);
    }
  }} onDragLeave={() => {
    dragDepth.current--;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  }} onDragOver={e => {
    if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
  }} onDrop={e => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const files = [...(e.dataTransfer?.files || [])];
    if (files.length) {
      setPending(files);
      setPastedText('');
      setPanel({ type: 'send-options' });
    }
  }}>
    <div class="app-content" inert={panel ? true : undefined}>
    <header class="site-header"><a class="brand" href={location.search || '/'} aria-label="Drop home"><span class="brand-mark"><Icon name="drop" size={25} /></span><span>Drop<span class="brand-period">.</span></span></a><div class={`connection-status ${state.status === 'online' ? '' : 'not-online'}`} role="status"><span class="status-dot" />{state.status === 'online' ? 'Connected' : state.status === 'reconnecting' ? 'Reconnecting…' : state.status === 'offline' ? 'Offline' : 'Connecting…'}</div><nav aria-label="App tools"><button class="icon-button" aria-label="Transfer history" title="Transfer history" onClick={() => setPanel({
            type: 'history'
          })}><Icon name="history" size={21} /></button><button class="icon-button" aria-label="Settings" title="Settings" onClick={() => setPanel({
            type: 'settings'
          })}><Icon name="settings" size={21} /></button></nav></header>
    <main><h1 class="sr-only">Nearby devices</h1>
    <section class={`radar-section ${dragging ? 'is-dragging' : ''}`} aria-label="Nearby devices"><div class="radar-toolbar"><div><span class="eyebrow">{state.room ? `ROOM ${state.room.code}` : 'NEARBY DEVICES'}</span><span class="device-count">{ready ? `${ready} ${ready === 1 ? 'device' : 'devices'} ready` : 'Looking for devices'}</span></div><button class={`select-button ${selecting ? 'active' : ''}`} aria-pressed={selecting} onClick={() => {
              setSelecting(!selecting);
              setSelection([]);
            }}><Icon name={selecting ? 'close' : 'select'} size={15} />{selecting ? 'Done' : 'Select'}</button></div>
    <div class={`radar ${!ready ? 'searching' : ''} ${radarCount > 8 ? 'radar-many' : radarCount > 4 ? 'radar-several' : ''}`}><div class="radar-rings" aria-hidden="true"><div class="radar-ring ring-outer" /><div class="radar-ring ring-middle" /><div class="radar-ring ring-inner" /><div class="radar-center-glow" /></div><div class="you"><span class="you-orbit"><Avatar animal={state.me.avatar} name={state.me.name} color={state.me.color} size={62} /><span class="you-dot" /></span><strong>You</strong><span>{state.me.name}</span></div>{radarPeers.filter(entry => entry.slot < 12).map(({
              peer,
              slot,
              leaving
            }) => <PeerButton key={peer.id} peer={peer} index={slot} count={radarCount} leaving={leaving} transfer={active.find(t => t.peerId === peer.id)} selecting={selecting} selected={selection.includes(peer.id)} onClick={() => handlePeer(peer)} onText={() => setPanel({
              type: 'text',
              ids: [peer.id]
            })} onDrop={files => {
              setDragging(false);
              dragDepth.current = 0;
              void send([peer.id], files);
            }} />)}</div>
    {radarPeers.some(entry => entry.slot >= 12) && <div class="extra-peers">{radarPeers.filter(entry => entry.slot >= 12).map(({
              peer,
              slot,
              leaving
            }) => <PeerButton key={peer.id} peer={peer} index={slot} count={radarCount} leaving={leaving} selecting={selecting} selected={selection.includes(peer.id)} onClick={() => handlePeer(peer)} onText={() => setPanel({
              type: 'text',
              ids: [peer.id]
            })} onDrop={files => void send([peer.id], files)} />)}</div>}
    {pending.length || pastedText ? (
      <div class="pending-banner">
        <span class="pending-symbol">
          <Icon name={pending.length ? 'file' : 'text'} size={20} />
        </span>
        <div>
          <strong>
            {pending.length
              ? `${pending.length} ${pending.length === 1 ? 'file' : 'files'} ready to go`
              : 'Your note is ready'}
          </strong>
          <span>Tap a device to pass it along.</span>
        </div>
        {pending.length > 0 && <button class="text-button pending-options" onClick={() => setPanel({ type: 'send-options' })}>Options</button>}
        <button
          class="icon-button"
          aria-label="Discard shared content"
          onClick={() => {
            setPending([]);
            setPastedText('');
          }}
        >
          <Icon name="close" size={17} />
        </button>
      </div>
    ) : !ready ? (
      <div class="empty-state">
        <p>
          Open Drop on another device on the same Wi-Fi,
          <br />
          or use an invite to connect.
        </p>
        <div class="empty-state-actions">
          <button class="button secondary" onClick={chooseFilesForShare}>
            <Icon name="link" size={15} /> Share link
          </button>
          <button class="button secondary" onClick={chooseFilesForDrop}>
            <Icon name="clock" size={15} /> Send later
          </button>
        </div>
        {state.room && <QR value={state.room.url} />}
      </div>
    ) : (
      <p class="radar-hint">
        {selecting ? (
          selection.length ? (
            `${selection.length} ${selection.length === 1 ? 'device' : 'devices'} selected. Ready when you are.`
          ) : (
            'Choose a few devices. Share something together.'
          )
        ) : (
          <>
            <span>Tap a device to send</span>
            <span class="hint-dot">·</span>
            <span>Hold to send a note</span>
          </>
        )}
      </p>
    )}
    {selecting && <div class="selection-actions"><button class="button primary" disabled={!selection.length} onClick={() => pending.length ? void send(selection, pending) : pastedText ? void sendText(selection, pastedText) : chooseFiles(selection)}>{pending.length || pastedText ? 'Send' : 'Choose files'}{selection.length > 0 && ` · ${selection.length}`}<Icon name="arrow" size={16} /></button><button class="icon-button" disabled={!selection.length} aria-label="Send text to selected devices" onClick={() => setPanel({
              type: 'text',
              ids: selection
            })}><Icon name="text" /></button><button class="icon-button desktop-folder" disabled={!selection.length} aria-label="Send folder to selected devices" onClick={() => chooseFiles(selection, true)}><Icon name="folder" /></button></div>}
    </section>
    {(active.length > 0 || incoming.length > 0) && <section class="transfer-stack" aria-label="Transfers">{incoming.map(t => <button class="incoming-banner" onClick={() => setPanel({
            type: 'incoming',
            id: t.id
          })}><Icon name="arrowDown" size={19} /><span><strong>{t.peerName} is sharing {t.files.length} {t.files.length === 1 ? 'file' : 'files'}</strong><small>Tap to take a look</small></span><Icon name="chevron" size={17} /></button>)}{active.map(t => <div class="transfer-bar" key={t.id}><div class="transfer-bar-fill" style={{
              width: `${Math.max(0, Math.min(100, t.bytesDone / (t.totalBytes || 1) * 100))}%`
            }} /><div class="transfer-direction"><Icon name={t.direction === 'receive' ? 'arrowDown' : 'arrow'} size={18} /></div><div class="transfer-info"><strong>{t.state === 'paused' ? 'Reconnecting…' : t.state === 'waiting' ? `Waiting for ${t.peerName}` : `${t.direction === 'receive' ? 'From' : 'To'} ${t.peerName}`}</strong><span>{t.state === 'waiting' ? 'They’ll be asked to accept.' : `${bytes(t.bytesDone)} / ${bytes(t.totalBytes)} · ${bytes(t.bytesPerSec)}/s · ${eta(t.etaSec)}`}</span></div><span class="path-badge">{t.path === 'connecting' ? 'Connecting' : t.path === 'turn' ? 'TURN' : t.path === 'direct' ? 'Direct' : 'Relay'}</span><button class="icon-button" aria-label={`Cancel transfer with ${t.peerName}`} onClick={() => client.cancel(t.id)}><Icon name="close" size={16} /></button></div>)}</section>}
    <section class="connect-actions" aria-label="Connect devices">
      <button onClick={() => setPanel({ type: 'pair' })}>
        <span class="action-symbol"><Icon name="link" size={20} /></span>
        <span><strong>Pair a device</strong><small>Keep your favorites close</small></span>
        <Icon name="chevron" size={15} />
      </button>
      <div class="action-divider" />
      <button onClick={() => setPanel({ type: 'room' })}>
        <span class="action-symbol"><Icon name="plus" size={22} /></span>
        <span>
          <strong>{state.room ? `Room ${state.room.code}` : 'Join a room'}</strong>
          <small>{state.room ? 'Your shared little space' : 'Share from anywhere'}</small>
        </span>
        <Icon name="chevron" size={15} />
      </button>
    </section>
    {(ready > 0 || pending.length > 0 || Boolean(pastedText) || state.shareLinks.length > 0) && <button class="send-entry text-button" onClick={() => setPanel({ type: 'send-options' })}><Icon name="share" size={16} />Share files or send later<Icon name="chevron" size={13} /></button>}
    <button class="discovery-help text-button" onClick={invite}><Icon name="wifi" size={16} />Not seeing your device?<Icon name="chevron" size={13} /></button>
    <p class="desktop-tip">Drop files anywhere, or paste something to share.</p>
    </main>
    </div>
    <input ref={fileInput} type="file" multiple hidden aria-label="Choose files to send" onChange={e => {
      const files = [...(e.currentTarget.files || [])];
      e.currentTarget.value = '';
      if (!files.length) return;
      if (pendingAction.current === 'share') {
        pendingAction.current = 'send';
        setPanel({ type: 'share-create', files });
      } else if (pendingAction.current === 'drop') {
        pendingAction.current = 'send';
        setPanel({ type: 'send-later', files });
      } else if (targetIds.current.length) {
        void send(targetIds.current, files);
      } else {
        setPending(files);
        setPanel({ type: 'send-options' });
      }
    }} /><input ref={folderInput} type="file" multiple hidden {...{
      webkitdirectory: '',
      directory: ''
    }} aria-label="Choose folder to send" onChange={e => {
      void send(targetIds.current, [...(e.currentTarget.files || [])]);
      e.currentTarget.value = '';
    }} />
    {panel && <Sheet key={`${panel.type}-${panel.id || ''}`} title={titles[panel.type]} eyebrow={panel.type === 'settings' ? 'SETTINGS' : panel.type === 'history' ? 'YOUR HISTORY' : panel.type === 'pair' ? 'PAIR A DEVICE' : panel.type === 'room' ? 'A SHARED SPACE' : undefined} onClose={close} className={panel.type === 'settings' ? 'settings-sheet' : ''}>
      {panel.type === 'send-options' && <><p class="sheet-description">{pending.length ? `${pending.length} files ready. How would you like to share?` : 'A nearby device, a simple link, or a little something for later.'}</p><div class="send-options">
        <button class="room-create" onClick={() => { close(); if (!pending.length) pickFor('send'); else notice('Tap a device to send your files'); }}><span class="action-symbol"><Icon name="phone" /></span><span><strong>Send to a device</strong><small>Choose a nearby or paired device</small></span><Icon name="chevron" size={16} /></button>
        <button class="room-create" onClick={chooseFilesForShare}><span class="action-symbol"><Icon name="link" /></span><span><strong>Create a share link</strong><small>Keep this tab open while they download</small></span><Icon name="chevron" size={16} /></button>
        <button class="room-create" onClick={chooseFilesForDrop}><span class="action-symbol"><Icon name="clock" /></span><span><strong>Send later</strong><small>Encrypted files, available for 24 hours</small></span><Icon name="chevron" size={16} /></button>
      </div>{state.shareLinks.map(link => <button class="text-button full" onClick={() => setPanel({ type: 'share', shareLink: link })}>Manage link · {link.files.length} files</button>)}</>}
      {panel.type === 'pair'  && <PairPanel client={client} initialCode={panel.code} notice={notice} close={close} />}
      {panel.type === 'room' && <RoomPanel client={client} state={state} notice={notice} />}
      {panel.type === 'settings' && <SettingsPanel client={client} state={state} notice={notice} />}
      {panel.type === 'history' && <HistoryPanel client={client} state={state} openTransfer={t => setPanel({
        type: 'completed',
        id: t.id
      })} openMessage={m => setPanel({
        type: 'message',
        id: m.id
      })} resend={resend} />}
      {panel.type === 'incoming' && transfer && <Incoming transfer={transfer} peer={state.peers.find(p => p.id === transfer.peerId)} client={client} close={close} />}
      {panel.type === 'completed' && transfer && <Completed transfer={transfer} client={client} notice={notice} />}
      {(panel.type === 'text' || panel.type === 'message') && <TextPanel message={message} peer={state.peers.find(p => p.id === (message?.peerId || panel.ids?.[0]))} ids={panel.ids || []} client={client} notice={notice} close={close} />}
      {panel.type === 'share-create' && (
        <CreateShareLinkPanel
          files={panel.files || pending}
          chooseFiles={() => pickFor('share')}
          client={client}
          onCreated={(link) => setPanel({ type: 'share', shareLink: link })}
          notice={notice}
        />
      )}
      {panel.type === 'share' && (panel.shareLink || state.shareLinks[0]) && (
        <ShareLinkPanel
          link={state.shareLinks.find(link => link.id === panel.shareLink?.id) || panel.shareLink || state.shareLinks[0]}
          client={client}
          notice={notice}
          close={close}
        />
      )}
      {panel.type === 'share-recipient' && panel.id && (
        <ShareRecipientPanel
          shareId={panel.id}
          client={client}
          state={state}
          notice={notice}
          close={close}
        />
      )}
      {panel.type === 'send-later' && (
        <SendLaterPanel
          files={panel.files || pending}
          chooseFiles={() => pickFor('drop')}
          notice={notice}
          close={close}
        />
      )}
      {panel.type === 'drop-recipient' && panel.id && (
        <DropRecipientPanel
          dropId={panel.id}
          dropKey={panel.code || ''}
          notice={notice}
          close={close}
        />
      )}
    </Sheet>}
    {toast && <div class="toast" role="status"><span>{toast}</span><button aria-label="Dismiss notification" onClick={() => setToast('')}><Icon name="close" size={15} /></button></div>}
    {dragging && <div class="drop-hint" aria-hidden="true"><Icon name="download" size={25} /><span>A little drop goes a long way.</span><small>Drop on a device, or anywhere to choose.</small></div>}
    <div class="sr-only" aria-live="polite" aria-atomic="true">{active.map(t => `${t.direction === 'send' ? 'Sending to' : 'Receiving from'} ${t.peerName}: ${t.state === 'paused' ? 'Reconnecting' : t.state === 'waiting' ? 'Waiting for acceptance' : `${Math.floor(t.bytesDone / (t.totalBytes || 1) * 10) * 10} percent`}`).join('. ')}</div>
  </div>;
}
